import "../../../App.css";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Descriptions, Form, Input, Layout, message, Modal, Radio, Result, Spin, Typography} from "antd";
import "antd/dist/reset.css";
import useAxios from "axios-hooks";
import {CheckoutSubmissionRequest} from "../../../types/CheckoutSubmissionRequest";
import {useOrderId} from "../../../hooks/UseOrderId";
import {PaymentType} from "../../../App";
import {CardTypeEnum} from "../../../types/CardTypeEnum";
import {CheckoutStatusResult} from "../../../types/CheckoutStatusResult";
import {ProviderTypeEnum} from "../../../types/ProviderTypeEnum";
import WrappedCard from "../../antd/WrappedCard";
import easypaisaLogo from "../../../assets/easypaisa-logo4.png";
import jazzcashLogo from "../../../assets/jazzcash-logo.png";
import "./PkRouteSettingMode.css";
import IntentApp from "../shared/IntentApp";
import teachingVideo from "../../../assets/bankCardTeching.mp4";
import bankCardLogo from "../../../assets/bankcard-logo.png";
import {Copyable} from "../../common/Copyable";
import jazzcashQrCodeLogo from "../../../assets/jazzcash-qrcode-logo.png";
import easypaisaQrCodeLogo from "../../../assets/easypaisa-qrcode-logo.png";
import {usePostCheckoutTypeApi} from "../../../api";

const {Content} = Layout;

const ignore = false;
const OverdueTime = 3 * 60 * 1000;
const PollingTime = 5 * 1000;
/** ResendOtp 冷却（秒）；首次 OTP 由 Deposit 触发 */
const ResendOtpCooldownSeconds = 180;
const DepositPollIntervalMs = 500;
const DepositPollMaxAttempts = 24;

// NOTICE: GET目前是同步的，后面有可能会需要支援非同步，近来收银台后先转圈等到GET有结果
function PkRouteSettingMode({getCheckoutInfoData}: PaymentType) {
    const id = useOrderId();

    const [{data: putData}, executePost] =
        useAxios<CheckoutSubmissionRequest>(
            {
                url: `/v2/checkout/${id}`,
                method: "POST"
            },
            {manual: true}
        );
    // console.log("[POST] checkout.putData", putData);

    const executePostWithRetry = async (postData: any) => {
        const retries = 3;
        const delay = 3000;
        for (let i = 0; i < retries; i++) {
            try {
                return await executePost(postData);
            } catch (error) {
                if (i < retries - 1) {
                    console.warn(`[POST RETRY] Attempt ${i + 1} failed, retrying in ${delay}ms...`, error);
                    await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                    console.error("[POST RETRY] All retry attempts failed.");
                    throw error;
                }
            }
        }
    };

    const [{data: getCheckoutResultData, loading: pollingLoading}, pollingCheckoutResult] =
        useAxios<CheckoutStatusResult>(`/v2/orders/${id}`, {manual: true});

    const {executePost: executeResendOtp} = usePostCheckoutTypeApi(id);

    // submitCheckout=true：waiting UI + 轮询（与现载一致）
    const [submitCheckout, setSubmitCheckout] = useState(getCheckoutInfoData?.checkoutStatus === "SUBMITTED");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isOtpPhase, setIsOtpPhase] = useState(false);
    const [otpSubmitting, setOtpSubmitting] = useState(false);
    const [otpResending, setOtpResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [otpForm] = Form.useForm();
    const lastWalletSubmitRef = useRef<Record<string, any> | null>(null);
    /** otpVerify poll：提交后先 ignoreOtpMessage，等旧 hint 清空后再认新的 otpMessage */
    const otpVerifyPollRef = useRef<{ ignoreOtpMessage: boolean } | null>(null);

    // overdue 用 ref，才能在进 OTP / 再进 waiting 时真正 clear
    const overdueCountDownRef = useRef<NodeJS.Timeout | null>(null);
    const [isOverdueTime, setIsOverdueTime] = useState<boolean>(false);

    const clearOverdueCountDown = useCallback(() => {
        if (overdueCountDownRef.current) {
            clearTimeout(overdueCountDownRef.current);
            overdueCountDownRef.current = null;
        }
    }, []);

    const startOverdueCountDown = useCallback(() => {
        clearOverdueCountDown();
        console.log("[CountDown] start to count down");
        overdueCountDownRef.current = setTimeout(function () {
            console.log("[CountDown] end to count down");
            overdueCountDownRef.current = null;
            setIsOverdueTime(true);
        }, OverdueTime);
    }, [clearOverdueCountDown]);
    const [providerName] = useState(
        getCheckoutInfoData?.defaultChannel === "easypaisa" ? CardTypeEnum.EasyPaisa :
            getCheckoutInfoData?.defaultChannel === "jazzcash" ? CardTypeEnum.JazzCash :
                getCheckoutInfoData?.defaultChannel === "card_group:bank" ? CardTypeEnum.BankCard :
                    getCheckoutInfoData?.defaultChannel === "card_group:sadapay" ? CardTypeEnum.SadapayCard :
                        getCheckoutInfoData?.defaultChannel === "card_group:opay" ? CardTypeEnum.OpayCard :
                            getCheckoutInfoData?.defaultChannel === "qrcode:easypaisa" ? CardTypeEnum.EasyPaisaQRCode:
                                getCheckoutInfoData?.defaultChannel === "qrcode:jazzcash" ? CardTypeEnum.JazzCashQRCode:
                            null);
    const [form] = Form.useForm();
    const [isFormValid, setIsFormValid] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [showBankCardHintModal, setShowBankCardHintModal] = useState(false);
    const [showIntentAppHintModal, setShowIntentAppHintModal] = useState(false);
    const [isAutoSubmit, setIsAutoSubmit] = useState(getCheckoutInfoData?.automaticSubmission);

    // 是否是钱包
    const [isWallet, setIsWallet] = useState(getCheckoutInfoData?.defaultChannel === "easypaisa" ||
        getCheckoutInfoData?.defaultChannel === "jazzcash");
    const [isJazzcash, setIsJazzcash] = useState(false);
    const [isEasypaisa, setIsEasypaisa] = useState(false);

    // 是否是卡片
    const [isCard, setIsCard] = useState(false);
    const [isBankCard, setIsBankCard] = useState(false);
    const [isSadapayCard, setIsSadapayCard] = useState(false);
    const [isOpayCard, setIsOpayCard] = useState(false);

    // 是否是 QR Code
    const [isQRCode, setIsQRCode] = useState(getCheckoutInfoData?.defaultChannel === "qrcode:easypaisa" ||
        getCheckoutInfoData?.defaultChannel === "qrcode:jazzcash");
    const [isJazzcashQRCode, setIsJazzcashQRCode] = useState(false);
    const [isEasypaisaQRCode, setIsEasypaisaQRCode] = useState(false);

    const [neverCountDown, setNeverCountDown] = useState(true);
    const [countDownTextOfWallet, setCountDownTextOfWallet] = useState("Ready countdown");
    const [countDownTextOfCard, setCountDownTextOfCard] = useState("Ready countdown");

    // 记录实际提交的支付渠道，用于倒数计时（form 在提交后会卸载，不能依赖 getFieldValue）
    const submittedProviderRef = React.useRef<string | undefined>(undefined);

    const walletSubmitBase = () => lastWalletSubmitRef.current || {
        providerType: ProviderTypeEnum.WALLET,
        providerName: providerName,
        customerAccount: getCheckoutInfoData?.receiverAccount,
        customerIdCard: getCheckoutInfoData?.receiverIdCard,
        customerName: getCheckoutInfoData?.receiverName,
        reserved1: getCheckoutInfoData?.reserved1,
        reserved2: getCheckoutInfoData?.reserved2,
        submitFieldType: "VOID"
    };

    const watchedProvider = Form.useWatch("providerName", form);
    const providerForKey = watchedProvider ?? providerName;
    const onceKey = React.useMemo(
        () => `${id}:${providerForKey ?? "unknown"}`,
        [id, providerForKey]
    );

    const openIntentOnce = React.useCallback(
        (source?: string) => {
            const storeKey = `intent-fired:${onceKey}`;
            if (sessionStorage.getItem(storeKey)) {
                console.log("[openIntentOnce] skip (already fired)", storeKey, "from", source);
                return;
            }
            sessionStorage.setItem(storeKey, "1");
            setShowIntentAppHintModal(true);
            console.log("[openIntentOnce] fired", storeKey, "from", source);
        },
        [onceKey, setShowIntentAppHintModal]
    );

    const cancelWalletIntent = React.useCallback(() => {
        sessionStorage.removeItem(`intent-fired:${onceKey}`);
        setShowIntentAppHintModal(false);
    }, [onceKey]);

    const isWalletProvider = (name?: string | null) =>
        name === CardTypeEnum.EasyPaisa || name === CardTypeEnum.JazzCash
        || name === "easypaisa" || name === "jazzcash";

    const enterWaitingPhase = (options?: { openWalletIntent?: boolean }) => {
        setIsOtpPhase(false);
        setSubmitCheckout(true);
        setIsSubmitting(false);
        setOtpSubmitting(false);
        clearOverdueCountDown();
        setIsOverdueTime(false);
        startOverdueCountDown();
        if (options?.openWalletIntent) {
            openIntentOnce("waiting");
        }
        pollingCheckoutResult().catch(() => undefined);
    };

    const enterOtpPhase = useCallback((options?: {showInvalidOtpError?: boolean; otpMessage?: string}) => {
        cancelWalletIntent();
        clearOverdueCountDown();
        setIsOverdueTime(false);
        setIsOtpPhase(true);
        setSubmitCheckout(false);
        setIsSubmitting(false);
        setOtpSubmitting(false);
        setResendCooldown((prev) => (prev > 0 ? prev : ResendOtpCooldownSeconds));
        if (options?.showInvalidOtpError) {
            messageApi.error(options.otpMessage || "Invalid OTP");
        }
    }, [cancelWalletIntent, clearOverdueCountDown, messageApi]);

    const isDepositPollSettled = (status?: CheckoutStatusResult) =>
        status?.status === "SUCCESS" ||
        status?.status === "FAIL" ||
        status?.submitStatus === "SUCCESS" ||
        status?.submitStatus === "FAIL";

    const isDepositFailed = (status?: CheckoutStatusResult) =>
        status?.status === "FAIL" || status?.submitStatus === "FAIL";

    type CheckoutPostPollMode = "deposit" | "otpVerify";

    const evaluateOtpVerifyPoll = (status?: CheckoutStatusResult): "pending" | "success" | "failed" => {
        const ctx = otpVerifyPollRef.current;
        if (!ctx) {
            return "pending";
        }
        if (!status?.otpRequired) {
            return "success";
        }
        if (!status.otpMessage) {
            ctx.ignoreOtpMessage = false;
            return "pending";
        }
        return ctx.ignoreOtpMessage ? "pending" : "failed";
    };

    /** POST 后 poll（MQ 异步）：deposit 遇 otpRequired→OTP；otpVerify 见新 otpMessage→错 OTP */
    const pollAfterCheckoutPost = async (mode: CheckoutPostPollMode) => {
        const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
        try {
            let lastStatus: CheckoutStatusResult | undefined;
            for (let attempt = 0; attempt < DepositPollMaxAttempts; attempt++) {
                if (attempt > 0) {
                    await sleep(DepositPollIntervalMs);
                }
                const response = await pollingCheckoutResult();
                lastStatus = response?.data;
                if (mode === "otpVerify") {
                    if (isDepositFailed(lastStatus)) {
                        otpVerifyPollRef.current = null;
                        enterOtpPhase({
                            showInvalidOtpError: true,
                            otpMessage: lastStatus?.otpMessage
                        });
                        return;
                    }
                    const otpVerifyOutcome = evaluateOtpVerifyPoll(lastStatus);
                    if (otpVerifyOutcome === "success") {
                        otpVerifyPollRef.current = null;
                        break;
                    }
                    if (otpVerifyOutcome === "failed") {
                        otpVerifyPollRef.current = null;
                        enterOtpPhase({
                            showInvalidOtpError: true,
                            otpMessage: lastStatus?.otpMessage
                        });
                        return;
                    }
                    continue;
                }
                if (lastStatus?.otpRequired) {
                    enterOtpPhase();
                    return;
                }
                if (isDepositPollSettled(lastStatus)) {
                    break;
                }
            }
            if (lastStatus?.otpRequired) {
                const showOtpVerifyError = mode === "otpVerify" && evaluateOtpVerifyPoll(lastStatus) === "failed";
                otpVerifyPollRef.current = null;
                enterOtpPhase(showOtpVerifyError ? {
                    showInvalidOtpError: true,
                    otpMessage: lastStatus?.otpMessage
                } : undefined);
                return;
            }
            otpVerifyPollRef.current = null;
            if (isDepositFailed(lastStatus)) {
                enterWaitingPhase({openWalletIntent: false});
                messageApi.error("Payment failed");
                return;
            }
            const provider = mode === "deposit"
                ? (submittedProviderRef.current ?? providerName)
                : (walletSubmitBase().providerName ?? providerName);
            enterWaitingPhase({openWalletIntent: isWalletProvider(provider)});
        } catch {
            otpVerifyPollRef.current = null;
            if (mode === "deposit") {
                setIsSubmitting(false);
            } else {
                setOtpSubmitting(false);
            }
        }
    };

    // otpRequired → OTP 页（刷新恢复 / Resend 后同步）；Deposit/Verify poll 内也会 enterOtpPhase
    useEffect(() => {
        if (!getCheckoutResultData?.otpRequired || isSubmitting || otpSubmitting) {
            return;
        }
        if (isOtpPhase) {
            return;
        }
        enterOtpPhase();
    }, [getCheckoutResultData?.otpRequired, isSubmitting, otpSubmitting, isOtpPhase, enterOtpPhase]);

    useEffect(() => {
        if (resendCooldown <= 0) {
            return;
        }
        const timer = setTimeout(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    // mount 时拉一次订单状态（OTP 恢复 / waiting 刷新）；manual 避免与 useAxios 默认请求重复
    useEffect(() => {
        pollingCheckoutResult().catch(() => undefined);
    }, []);

    const onFormFinish = (values: any) => {
        submittedProviderRef.current = values?.providerName;
        setIsSubmitting(true);

        if (!ignore) {
            let submitFieldType;
            switch (values.providerName) {
                case CardTypeEnum.EasyPaisa:
                    submitFieldType = getCheckoutInfoData?.easypaisaSubmitFieldType;
                    break;
                case CardTypeEnum.JazzCash:
                    submitFieldType = getCheckoutInfoData?.jazzcashSubmitFieldType;
                    break;
                default:
                    submitFieldType = "VOID";
                    break;
            }

            const postBody = {
                id: id,
                providerType: ProviderTypeEnum.WALLET,
                providerName: values.providerName,
                customerAccount: values.customerAccount,
                customerIdCard: values.customerIdCard,
                customerName: values.customerName,
                reserved1: getCheckoutInfoData?.reserved1,
                reserved2: getCheckoutInfoData?.reserved2,
                automaticSubmission: getCheckoutInfoData?.automaticSubmission,
                submitFieldType: submitFieldType
            };
            lastWalletSubmitRef.current = postBody;

            executePostWithRetry({
                data: postBody
            })
                .then(() => pollAfterCheckoutPost("deposit"))
                .catch((error) => {
                    setIsSubmitting(false);
                    messageApi.error("Failed:" + error?.response?.data?.title);
                });
        }
    };

    const onOtpFinish = (values: any) => {
        otpVerifyPollRef.current = { ignoreOtpMessage: true };
        setOtpSubmitting(true);
        executePostWithRetry({
            data: {
                ...walletSubmitBase(),
                otp: values.otp
            }
        })
            .then(() => pollAfterCheckoutPost("otpVerify"))
            .catch((error) => {
                messageApi.error("Failed:" + (error?.response?.data?.title || "Invalid OTP"));
                setOtpSubmitting(false);
            });
    };

    const onResendOtp = () => {
        if (otpResending || resendCooldown > 0) {
            return;
        }
        const base = walletSubmitBase();
        setOtpResending(true);
        executeResendOtp({
            data: {
                providerType: base.providerType || ProviderTypeEnum.WALLET,
                providerName: base.providerName,
                customerAccount: base.customerAccount,
                customerIdCard: base.customerIdCard,
                customerName: base.customerName
            }
        })
            .then((res: any) => {
                messageApi.success(res?.data?.message || "OTP resent");
                setResendCooldown(ResendOtpCooldownSeconds);
                pollingCheckoutResult().catch(() => undefined);
            })
            .catch((error) => {
                messageApi.error("Failed:" + (error?.response?.data?.title || "Resend OTP failed"));
            })
            .finally(() => {
                setOtpResending(false);
            });
    };

    const onButtonClick = () => {
        form.submit();
    };

    const checkFormValidity = () => {
        form.validateFields()
            .then(() => {
                setIsFormValid(true);
            })
            .catch(() => {
                setIsFormValid(false);
            });
    }

    const refreshPayType = () => {
        refreshField();
        checkFormValidity();
    }

    // 依照选项显示不同的内容
    const refreshField = () => {
        let cardType = form.getFieldValue("providerName");

        setIsWallet(false);
        setIsJazzcash(false);
        setIsEasypaisa(false);

        setIsCard(false);
        setIsBankCard(false);
        setIsSadapayCard(false);
        setIsOpayCard(false);

        setIsQRCode(false);
        setIsJazzcashQRCode(false);
        setIsEasypaisaQRCode(false);

        if (cardType === CardTypeEnum.JazzCash){
            setIsWallet(true);
            setIsJazzcash(true);
        } else if (cardType === CardTypeEnum.EasyPaisa){
            setIsWallet(true);
            setIsEasypaisa(true);
        } else if (cardType === CardTypeEnum.BankCard){
            setIsCard(true);
            setIsBankCard(true);
        }else if (cardType === CardTypeEnum.SadapayCard){
            setIsCard(true);
            setIsSadapayCard(true);
        }else if (cardType === CardTypeEnum.OpayCard){
            setIsCard(true);
            setIsOpayCard(true);
        }else if (cardType === CardTypeEnum.EasyPaisaQRCode){
            setIsQRCode(true);
            setIsEasypaisaQRCode(true);
        }else if (cardType === CardTypeEnum.JazzCashQRCode){
            setIsQRCode(true);
            setIsJazzcashQRCode(true);
        }
    }

    const submitCountDownOfWallet = () => {
        const selectedProvider = submittedProviderRef.current ?? providerName;
        const isEp = selectedProvider === CardTypeEnum.EasyPaisa || selectedProvider === "easypaisa";
        const countDownSeconds = isEp ? 1.5 * 60 : 3 * 60;
        let num = countDownSeconds;
        setCountDownTextOfWallet(formatTime(num));
        let timer = setInterval(() => {
            num--;
            setCountDownTextOfWallet(formatTime(num));
            if (num === countDownSeconds - 5){
                // 倒数几秒后发动自动唤醒
                openIntentOnce("countdown");
            }
            if (num === 0) {
                setCountDownTextOfWallet("Time's Up !!!");
                clearInterval(timer);
            }
        }, 1000);
    };

    const submitCountDownOfCard = () => {
        let num = 30 * 60;
        setCountDownTextOfCard(formatTime(num));
        let timer = setInterval(() => {
            num--;
            setCountDownTextOfCard(formatTime(num));
            if (num === 0) {
                setCountDownTextOfCard("Time's Up !!!");
                clearInterval(timer);
            }
        }, 1000);
    };

    const formatTime = (seconds: number) => {
        let minutes = Math.floor(seconds / 60);
        let remainingSeconds = seconds % 60;
        return `${padZero(minutes)}:${padZero(remainingSeconds)}`;
    };

    const padZero = (num: number) => {
        return num < 10 ? `0${num}` : `${num}`;
    };

    // NOTICE: 十秒轮询
    // 条件：submitCheckout && !isOverdueTime && status===PROCESSING（OTP 页 submitCheckout=false，不会进）
    useEffect(() => {
        const stoppingStatuses: string[] = ["SUCCESS", "FAIL"]

        let pollingCountDown: NodeJS.Timeout;
        if (isOverdueTime) {
            console.log(
                "[Polling] Cause overdue time , so no need to process to poll"
            );
        } else if (!submitCheckout) {
            refreshPayType();
            console.log("[Polling] no need to process to poll");
        } else {
            console.log("[Polling] pollingLoading", pollingLoading);
            if (getCheckoutResultData?.status === "PROCESSING") {
                console.log("[Polling] restart to polling");

                if (neverCountDown){
                    setNeverCountDown(false);
                    submitCountDownOfWallet();
                    submitCountDownOfCard();
                }

                pollingCountDown = setTimeout(function () {
                    pollingCheckoutResult()
                        .then((data) => {
                            console.log("[Polling] end to polling.data", data);
                        })
                        .catch((error) => {
                            console.log("[Polling] restart to polling.error", error);
                        })
                        .finally(() => {
                        });
                }, PollingTime);
            } else if (typeof getCheckoutResultData !== "undefined" && stoppingStatuses.includes(getCheckoutResultData.status)) {
                console.log("[Polling] end to polling.data", getCheckoutResultData);
                messageApi.info("Result: " + getCheckoutResultData.status);
            }
        }
        return () => {
            if (pollingCountDown) clearTimeout(pollingCountDown);
        };
    }, [submitCheckout, pollingLoading, isOverdueTime, getCheckoutResultData]);

    useEffect(() => {
        // 与现载一致：仅未提交时自动提交；OTP 页不要触发
        if (!submitCheckout && isAutoSubmit && !isOtpPhase) {
            setIsAutoSubmit(false);
            onFormFinish({
                providerName: getCheckoutInfoData?.defaultChannel,
                customerAccount: getCheckoutInfoData?.receiverAccount,
                customerIdCard: getCheckoutInfoData?.receiverIdCard,
                customerName: getCheckoutInfoData?.receiverName,
            });
        }
    }, [isAutoSubmit]);

    const [{ data: qrBlob, loading: qrLoading, error: qrError }, fetchQrCode] =
        useAxios<Blob>(
            {
                url: `/v2/checkout/qr-code/${id}`,
                method: "GET",
                responseType: "blob",
            },
            { manual: true }
        );

    const [qrImgUrl, setQrImgUrl] = useState<string | null>(null);

    useEffect(() => {
        return () => {
            clearOverdueCountDown();
        };
    }, [clearOverdueCountDown]);

    useEffect(() => {
        if (qrBlob) {
            const objectUrl = URL.createObjectURL(qrBlob);
            setQrImgUrl(objectUrl);

            return () => {
                URL.revokeObjectURL(objectUrl); // 防 memory leak
            };
        }
    }, [qrBlob]);

    useEffect(() => {
        if (submitCheckout && isQRCode) {
            fetchQrCode();
        }
    }, [submitCheckout, isQRCode]);

    return (
        <>
            {contextHolder}

            {/*NOTICE: 訂單未完成*/}
            {getCheckoutInfoData?.checkoutStatus === "VISITED" &&
                !submitCheckout &&
                !isOtpPhase && (
                    <Content>
                        <WrappedCard cardProps={{ className: "rs1-payment-container" }}>
                            <>
                                <div className="rs1-amount-content">
                                    <div className = "rs1-amount">
                                        {getCheckoutInfoData?.orderAmount}
                                    </div>

                                    <div className="rs1-amount-currency">
                                        Amount (PKR)
                                    </div>
                                    <div className="rs1-transaction-fee">
                                        <span style={{float: "left"}}>Transaction Fee</span>
                                        <span style={{float: "right"}}>Free</span>
                                    </div>
                                </div>
                            </>

                            <Spin spinning={isSubmitting} tip="Processing payment..." className="rs1-deposit-spin">
                            <Form
                                form={form}
                                initialValues={{providerName}}
                                onFinish={onFormFinish}
                                onChange={refreshPayType}
                            >
                                <>
                                    <div className="rs1-form-container">
                                        <div className="rs1-table-header">
                                            <span>Select Channel</span>
                                            <span>ادائیگی کی قسم منتخب کریں</span>
                                        </div>
                                        <Form.Item name="providerName" className="rs1-form-item">
                                            <Radio.Group className="rs1-radio-group">
                                                {getCheckoutInfoData?.availableChannels.includes("easypaisa") && (
                                                    <Radio.Button value={CardTypeEnum.EasyPaisa}
                                                                  className="rs1-radio-button">
                                                        <img src={easypaisaLogo} alt="Easypaisa"/><br/>
                                                        Easypaisa
                                                    </Radio.Button>)}
                                                {getCheckoutInfoData?.availableChannels.includes("jazzcash") && (
                                                    <Radio.Button value={CardTypeEnum.JazzCash}
                                                                  className="rs1-radio-button">
                                                        <img src={jazzcashLogo} alt="Jazzcash"/><br/>
                                                        Jazzcash
                                                    </Radio.Button>)}
                                                {getCheckoutInfoData?.availableChannels.includes("qrcode:easypaisa") && (
                                                    <Radio.Button value={CardTypeEnum.EasyPaisaQRCode}
                                                                  className="rs1-radio-button">
                                                        <img src={easypaisaQrCodeLogo} alt="EasyPaisa QRCode"/><br/>
                                                        Easypaisa
                                                    </Radio.Button>)}
                                                {getCheckoutInfoData?.availableChannels.includes("qrcode:jazzcash") && (
                                                    <Radio.Button value={CardTypeEnum.JazzCashQRCode}
                                                                  className="rs1-radio-button">
                                                        <img src={jazzcashQrCodeLogo} alt="JazzCash QRCode"/><br/>
                                                        Jazzcash
                                                    </Radio.Button>)}
                                                {getCheckoutInfoData?.availableChannels.includes("card_group:bank") &&
                                                    (<Radio.Button value={CardTypeEnum.BankCard}
                                                                   className="rs1-radio-button">
                                                        <img src={bankCardLogo} alt="card"/><br/>
                                                        Card
                                                    </Radio.Button>)}
                                                {getCheckoutInfoData?.availableChannels.includes("card_group:sadapay") &&
                                                    (<Radio.Button value={CardTypeEnum.SadapayCard}
                                                                   className="rs1-radio-button">
                                                        <img src={bankCardLogo} alt="card"/><br/>
                                                        Card
                                                    </Radio.Button>)}
                                                {getCheckoutInfoData?.availableChannels.includes("card_group:opay") &&
                                                    (<Radio.Button value={CardTypeEnum.OpayCard}
                                                                   className="rs1-radio-button">
                                                        <img src={bankCardLogo} alt="card"/><br/>
                                                        Card
                                                    </Radio.Button>)}
                                            </Radio.Group>
                                        </Form.Item>

                                        {/*银行卡收说明*/}
                                        {isBankCard && (
                                            <WrappedCard cardTitle="Information">
                                                <>
                                                    <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                        Step 1. Fill in payment information <br/>
                                                        Step 2. Pay within 30 minutes <br/>
                                                        Step 3. Please transfer fund to the following bank
                                                        account <br/>
                                                        <Button style={{
                                                            fontSize: 16,
                                                            color: "white",
                                                            background: "#1677ff",
                                                            textAlign: "center"
                                                        }} onClick={() => {
                                                            setShowBankCardHintModal(true)
                                                        }}>How to do it?</Button>
                                                        <Modal
                                                            open={showBankCardHintModal}
                                                            closable={false}
                                                            onOk={() => {
                                                                setShowBankCardHintModal(false)
                                                            }}
                                                            onCancel={() => {
                                                                setShowBankCardHintModal(false)
                                                            }}
                                                        >
                                                            <video controls width="100%">
                                                                <source src={teachingVideo} type="video/mp4"/>
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        </Modal>
                                                        <br/>
                                                    </div>
                                                </>
                                            </WrappedCard>
                                        )}

                                        {/* Sadapay卡收说明*/}
                                        {isSadapayCard && (
                                            <WrappedCard cardTitle="Information">
                                                <>
                                                    <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                        Step 1. Fill in payment information <br/>
                                                        Step 2. Pay within 30 minutes <br/>
                                                        Step 3. Please transfer fund to the following SadaPay
                                                        account <br/>
                                                    </div>
                                                </>
                                            </WrappedCard>
                                        )}

                                        {/* Opay卡收说明*/}
                                        {isOpayCard && (
                                            <WrappedCard cardTitle="Information">
                                                <>
                                                    <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                        Step 1. Fill in payment information <br/>
                                                        Step 2. Pay within 30 minutes <br/>
                                                        Step 3. Please transfer fund to the following OPay account <br/>
                                                    </div>
                                                </>
                                            </WrappedCard>
                                        )}

                                        {/* 钱包栏位*/}
                                        <div className="rs1-input-container">
                                            {((isEasypaisa && getCheckoutInfoData?.easypaisaSubmitFields.includes("customerAccount")) ||
                                                (isJazzcash && getCheckoutInfoData?.jazzcashSubmitFields.includes("customerAccount"))) && (
                                                <>
                                                    <div className="rs1-input-label">
                                                        <span>Wallet account number</span>
                                                        <span>والٹ اکاؤنٹ نمبر</span>
                                                    </div>
                                                    <Form.Item
                                                        name="customerAccount"
                                                        rules={[{
                                                            required: true,
                                                            message: "account should be an integer starting with 3 and totaling ten digits."
                                                        }]}
                                                        initialValue={getCheckoutInfoData?.receiverAccount}
                                                    >
                                                        <Input className="rs1-input-field"/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            {((isEasypaisa && getCheckoutInfoData?.easypaisaSubmitFields.includes("customerIdCard")) ||
                                                (isJazzcash && getCheckoutInfoData?.jazzcashSubmitFields.includes("customerIdCard"))) && (
                                                <>
                                                    <div className="rs1-input-label">
                                                        <span>CNIC</span>
                                                        <span>شناختی کارڈ</span>
                                                    </div>
                                                    <Form.Item
                                                        name="customerIdCard"
                                                        rules={[{
                                                            required: true,
                                                            message: "CNIC should be real CNIC number and thirteen digital integer."
                                                        }]}
                                                        initialValue={getCheckoutInfoData?.receiverIdCard}
                                                    >
                                                        <Input className="rs1-input-field"/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            {isCard && (
                                                <>
                                                    <div className="rs1-input-label">
                                                        <span>Account Name</span>
                                                        <span>اکاؤنٹ کا نام</span>
                                                    </div>

                                                    <Form.Item name="customerName"
                                                               rules={[{
                                                                   required: true,
                                                                   message: "Please insert correct name"
                                                               }]}
                                                               initialValue={getCheckoutInfoData?.receiverName}
                                                    >
                                                    <Input className="rs1-input-field"/>
                                                    </Form.Item>

                                                    <div className="rs1-input-label">
                                                        <span>Bank Account Number or Wallet Account</span>
                                                        <span>بینک اکاؤنٹ نمبر یا والیٹ اکاؤنٹ</span>
                                                    </div>

                                                    <Form.Item name="customerAccount"
                                                               rules={[{
                                                                   required: true,
                                                                   message: "Please insert correct account"
                                                               }]}
                                                               initialValue={getCheckoutInfoData?.receiverAccount}
                                                    >

                                                        <Input className="rs1-input-field"/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            {/*Jazzcash 钱包 QR Code 说明*/}
                                            {isJazzcashQRCode && (
                                                <WrappedCard cardTitle="Information">
                                                    <>
                                                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                            Step 1. Fill in wallet account <br/>
                                                            Step 2. Pay within 30 minutes <br/>
                                                            Step 3. Please turn on your jazzcash wallet app and scan QR code<br/>
                                                        </div>
                                                    </>
                                                </WrappedCard>
                                            )}

                                            {/*Easypaisa 钱包 QR Code说明*/}
                                            {isEasypaisaQRCode && (
                                                <WrappedCard cardTitle="Information">
                                                    <>
                                                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                            Step 1. Fill in wallet account <br/>
                                                            Step 2. Pay within 30 minutes <br/>
                                                            Step 3. Please turn on your easypaisa wallet app and scan QR code<br/>
                                                        </div>
                                                    </>
                                                </WrappedCard>
                                            )}

                                            {isQRCode && (
                                                <>
                                                    <div className="rs1-input-label">
                                                        <span>Wallet Account</span>
                                                        <span>بینک اکاؤنٹ نمبر یا والیٹ اکاؤنٹ</span>
                                                    </div>

                                                    <Form.Item name="customerAccount"
                                                               rules={[{
                                                                   required: true,
                                                                   message: "Please insert correct account"
                                                               }]}
                                                               initialValue={getCheckoutInfoData?.receiverAccount}
                                                    >

                                                        <Input className="rs1-input-field"/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            <Form.Item>
                                                <div className="rs1-submit-button-container">
                                                    <Button type="primary" className="rs1-submit-button"
                                                            onClick={onButtonClick}
                                                            disabled={!isFormValid || isSubmitting}>
                                                        {isSubmitting ? "Paying..." : "Pay"}
                                                    </Button>
                                                </div>
                                            </Form.Item>

                                            <p className="rs1-footer-text">
                                                After clicking "pay", please open your wallet and enter your password to
                                                complete the payment.<br/>
                                                براہ کرم پے کا بٹن دبانے کے بعد اپنا والیٹ اکاؤنٹ کھولیں پاسورڈ کے
                                                اندراج سے رقم کی ادائیگی مکمل کریں
                                            </p>
                                        </div>
                                    </div>
                                </>
                            </Form>
                            </Spin>
                        </WrappedCard>
                    </Content>
                )}

            {isOtpPhase && !submitCheckout && (
                <Content>
                    <WrappedCard cardProps={{className: "rs1-payment-container"}}>
                        <div className="rs1-amount-content">
                            <div className="rs1-amount">
                                {getCheckoutInfoData?.orderAmount}
                            </div>
                            <div className="rs1-amount-currency">
                                Amount (PKR)
                            </div>
                        </div>
                        <Form form={otpForm} onFinish={onOtpFinish} className="rs1-form-container">
                            <div className="rs1-input-container">
                                {getCheckoutResultData?.otpMessage && (
                                    <div className="rs1-otp-hint">
                                        {getCheckoutResultData.otpMessage}
                                    </div>
                                )}
                                <div className="rs1-input-label">
                                    <span>OTP</span>
                                    <span>او ٹی پی</span>
                                </div>
                                <Form.Item
                                    name="otp"
                                    rules={[{required: true, message: "OTP is required"}]}
                                    className="rs1-form-item"
                                >
                                    <Input
                                        className="rs1-input-field rs1-otp-input"
                                        placeholder="Enter OTP"
                                        maxLength={8}
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                    />
                                </Form.Item>
                                <div className="rs1-otp-actions">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        className="rs1-submit-button rs1-otp-submit"
                                        loading={otpSubmitting}
                                    >
                                        {otpSubmitting ? "Verifying..." : "Submit OTP"}
                                    </Button>
                                    <Button
                                        className="rs1-otp-resend"
                                        onClick={onResendOtp}
                                        loading={otpResending}
                                        disabled={resendCooldown > 0 || otpSubmitting}
                                    >
                                        {resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : "Resend OTP"}
                                    </Button>
                                </div>
                                <p className="rs1-footer-text">
                                    Enter the OTP sent to your wallet, then submit to complete payment.<br/>
                                    براہ کرم اپنے والیٹ پر موصول OTP درج کریں اور ادائیگی مکمل کرنے کے لیے جمع کرائیں۔
                                </p>
                            </div>
                        </Form>
                    </WrappedCard>
                </Content>
            )}

            {/*訂單已提交 (直连模式)*/}
            {submitCheckout && isWallet && (
                <>
                    <Result
                        status="info"
                        title={
                            <>
                                <div style={{textAlign: "center", color: "red"}}>
                                    Time Remaining<br/>
                                    {countDownTextOfWallet}
                                </div>
                                <div>
                                    The payment process has started. If there’s not automatically redirected, just open your {providerName===CardTypeEnum.JazzCash ? "JazzCash" : providerName===CardTypeEnum.EasyPaisa ? "Easypaisa" : ""} wallet app and approve the transaction.<br/>
                                    ادائیگی کا عمل شروع ہو گیا ہے۔ اگر خودکار ری ڈائریکٹ نہ ہو تو براہ کرم {providerName===CardTypeEnum.JazzCash ? "JazzCash" : providerName===CardTypeEnum.EasyPaisa ? "Easypaisa" : ""} والیٹ ایپ کھول کر لین دین کی منظوری دیں۔
                                </div>
                            </>
                        }
                    />

                    {/*为了提供唤醒的选项，仍需挂截 form */}
                    <Form form={form}>
                        <Form.Item name="providerName" initialValue={providerName}></Form.Item>
                    </Form>
                </>
            )}

            {/* 钱包：poll/OTP 后进 waiting 时弹 Intent；Deposit 期间仅 Paying... */}
            {submitCheckout && showIntentAppHintModal && isWallet && !isOtpPhase &&
                <IntentApp providerName={form.getFieldValue("providerName")}/>}

            {/*卡收提交后画面*/}
            {submitCheckout && isCard &&
                !isOverdueTime &&
                getCheckoutResultData?.status === "PROCESSING" && (
                    <Result
                        status="info"
                        title={
                            <>
                                <div style={{textAlign: "center", color: "red"}}>
                                    Time Remaining<br/>
                                    {countDownTextOfCard}
                                </div>
                                <div>
                                    Please pay within 30 minutes.<br/>
                                </div>
                            </>
                        }
                    />
                )}

            {/*银行卡收说明*/}
            {submitCheckout && isBankCard && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in payment information <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please transfer fund to the following bank
                            account <br/>
                            <Button style={{
                                fontSize: 16,
                                color: "white",
                                background: "#1677ff",
                                textAlign: "center"
                            }} onClick={() => {
                                setShowBankCardHintModal(true)
                            }}>How to do it?</Button>
                            <Modal
                                open={showBankCardHintModal}
                                closable={false}
                                onOk={() => {
                                    setShowBankCardHintModal(false)
                                }}
                                onCancel={() => {
                                    setShowBankCardHintModal(false)
                                }}
                            >
                                <video controls width="100%">
                                    <source src={teachingVideo} type="video/mp4"/>
                                    Your browser does not support the video tag.
                                </video>
                            </Modal>
                            <br/>
                        </div>
                    </>
                </WrappedCard>
            )}

            {/* Sadapay卡收说明*/}
            {submitCheckout && isSadapayCard && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in payment information <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please transfer fund to the following SadaPay
                            account <br/>
                        </div>
                    </>
                </WrappedCard>
            )}

            {/* Opay卡收说明*/}
            {submitCheckout && isOpayCard && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in payment information <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please transfer fund to the following OPay account <br/>
                        </div>
                    </>
                </WrappedCard>
            )}

            {submitCheckout && isCard && (
                <Descriptions title={
                    <>
                        <table width="100%">
                            <tbody>
                            <tr>
                                <td>Card Information</td>
                                <td align="right">بینک کی معلومات</td>
                            </tr>
                            </tbody>
                        </table>
                    </>
                } bordered>
                    <Descriptions.Item label={
                        <>
                            Amount<br/>
                            قابل ادائیگی رقم
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.orderAmount}
                        </Typography.Title>
                    </Descriptions.Item>

                    <Descriptions.Item label={
                        <>
                            Bank Account<br/>
                            بینک اکاؤنٹ
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverAccount}
                        </Typography.Title>
                    </Descriptions.Item>

                    <Descriptions.Item label={
                        <>
                            Bank Name<br/>
                            بینک کا نام
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverBankName}
                        </Typography.Title>
                    </Descriptions.Item>

                    {getCheckoutInfoData?.receiverBranchBank && (<Descriptions.Item label={
                        <>
                            Branch Bank Name<br/>
                            بینک برانچ کا نام
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverBankBranchName}
                        </Typography.Title>
                    </Descriptions.Item>)}

                    <Descriptions.Item label={
                        <>
                            Account Name<br/>
                            کھاتے کا نام
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverName}
                        </Typography.Title>
                    </Descriptions.Item>
                </Descriptions>
            )}

            {submitCheckout && isQRCode && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in wallet account <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please turn on your wallet app and scan QR code <br/>
                        </div>

                        <div style={{ textAlign: "center", marginTop: 12 }}>
                            {qrLoading && <Typography.Text>Loading QR Code...</Typography.Text>}

                            {qrError && (
                                <Typography.Text type="danger">
                                    Failed to load QR Code
                                </Typography.Text>
                            )}

                            {qrImgUrl && (
                                <img src={qrImgUrl} width={280} height={280} alt="QR Code" />
                            )}
                        </div>
                    </>
                </WrappedCard>
            )}
        </>
    );
}

export default PkRouteSettingMode;
